// src/components/FloatingWidgets.jsx
import React, { useEffect, useRef, useState } from "react";

/* Media query hook */
function useMediaQuery(query) {
  const getMatch = () =>
    typeof window !== "undefined" && window.matchMedia(query).matches;

  const [matches, setMatches] = useState(getMatch);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [query]);

  return matches;
}

/* Dependency-free draggable hook with "no-drag" zones */
function useDraggable(initial) {
  const ref = useRef(null);
  const [pos, setPos] = useState(initial);
  const [isDragging, setIsDragging] = useState(false);

  // Refs so we don't need to rebind listeners on every move
  const start = useRef(null);
  const origin = useRef({ x: initial.x, y: initial.y });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onPointerDown = (e) => {
      // Only primary button
      if (e.button !== 0) return;

      // Ignore clicks that start in "no-drag" areas (like our header / close button)
      if (e.target?.closest?.('[data-no-drag="true"]')) return;

      el.setPointerCapture?.(e.pointerId);
      start.current = { x: e.clientX, y: e.clientY };
      origin.current = { x: pos.x, y: pos.y };
      setIsDragging(true);
    };

    const onPointerMove = (e) => {
      if (!start.current) return;
      const dx = e.clientX - start.current.x;
      const dy = e.clientY - start.current.y;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const rect = el.getBoundingClientRect();
      const nextX = Math.min(Math.max(0, origin.current.x + dx), vw - rect.width);
      const nextY = Math.min(Math.max(0, origin.current.y + dy), vh - rect.height);
      setPos({ x: nextX, y: nextY });
    };

    const endDrag = (e) => {
      el.releasePointerCapture?.(e.pointerId);
      start.current = null;
      setIsDragging(false);
    };

    el.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);

    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("pointercancel", endDrag);
    };
  }, [pos.x, pos.y]);

  const style = {
    position: "fixed",
    left: pos.x,
    top: pos.y,
    touchAction: "none",
    cursor: isDragging ? "grabbing" : "grab",
    zIndex: 30,
    borderRadius: 8,
    boxShadow: "0 10px 30px rgba(0,0,0,.2)",
    background: "rgba(255,255,255,.85)",
    backdropFilter: "blur(6px)",
    overflow: "hidden"
  };

  return { ref, style, isDragging };
}



/** Draggable window with a close (×) that hides just this widget */
function DraggableWidget({ id, initial, children, onClose }) {
  const drag = useDraggable(initial);

  const headerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    height: 20,
    padding: "0",
    background: "#F9D957" // keeps it clickable above iframes
  };

  const closeBtnStyle = {
    appearance: "none",
    border: "none",
    background: "transparent",
    borderRadius: 6,
    fontSize: 20,
    margin: '0 10px',
    padding: 0,
    cursor: "pointer",
    userSelect: "none",
    color: '#000'
  };

  return (
    <div ref={drag.ref} style={drag.style} aria-label={`${id} widget`} role="dialog">
      {/* Header is now DRAGGABLE */}
      <div style={headerStyle}>
        <button
          type="button"
          aria-label={`Close ${id} widget`}
          title="Close"
          style={closeBtnStyle}
          data-no-drag="true"             // <-- only the button blocks dragging
          onPointerDown={(e) => { e.stopPropagation(); }}
          onMouseDown={(e) => { e.stopPropagation(); }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onClose?.(id);
          }}
        >
          ×
        </button>
      </div>
      <div>{children}</div>
    </div>
  );
}


export default function FloatingWidgets() {
  // Hooks FIRST (no conditional calls before returning)
  const wide = useMediaQuery("(min-width: 1200px)");
  const [closedIds, setClosedIds] = useState(() => new Set());

  // Safe even if we early-return later
  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;

  const widgets = [
    {
      id: "video",
      initial: { x: vw - 430, y: 420 },
      content: (
        <div style={{ width: 360, height: 210 }}>
          <iframe
            title="Inspiration"
            width="360px"
            height="210px"
            style={{border: 'none'}}
            src="https://www.youtube.com/embed/Jo8jmv4l2IE"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </div>
      )
    },
    {
      id: "soundcloud",
      initial: { x: 25, y: 140 },
      content: (
        <div style={{ width: 420, height: 166 }}>
            <iframe width="100%" height="166" scrolling="no" frameborder="no" allow="autoplay" src="https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/soundcloud%253Atracks%253A2071156812&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true"></iframe><div style={{fontSize: 10, color: '#cccccc', lineBreak: 'anywhere', wordBreak: "normal", overflow: 'hidden', whiteSpace: "nowrap", textOverflow: "ellipsis", fontFamily: "Interstate,Lucida Grande,Lucida Sans Unicode,Lucida Sans,Garuda,Verdana,Tahoma,sans-serif", fontWeight: '100',}}><a href="https://soundcloud.com/dadada_dada_da_da" title="Dadada" target="_blank" style={{color: '#cccccc', textDecoration: "none" }}>Dadada</a> · <a href="https://soundcloud.com/dadada_dada_da_da/ease-your-mind-exchange-mmm-44-16-mstrd" title="Ease Your Mind" target="_blank" style={{color: '#cccccc', textDecoration: "none" }}>Ease Your Mind</a></div>
        </div>
      )
    }
  ];

  const handleClose = (id) =>
    setClosedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });

  const visibleWidgets = widgets.filter((w) => !closedIds.has(w.id));

  // Return after hooks are declared to satisfy rules-of-hooks
  if (!wide) return null;

  return (
    <>
      {visibleWidgets.map((w) => (
        <DraggableWidget
          key={w.id}
          id={w.id}
          initial={w.initial}
          onClose={handleClose}
        >
          {w.content}
        </DraggableWidget>
      ))}
    </>
  );
}
